import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  DirectionsRenderer,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { authFetch } from "../lib/api";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Edit3,
  ExternalLink,
  Loader2,
  Map,
  MapPin,
  Plus,
  Search,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";

const WALK_TYPE_LABELS = {
  door_knock: "Door Knock",
  lit_drop: "Lit Drop",
  phone_bank: "Phone Bank",
  site_visit: "Site Visit",
  delivery_route: "Delivery Route",
};
const WALK_TYPE_BADGE = {
  door_knock: "bg-blue-100 text-blue-700",
  lit_drop: "bg-amber-100 text-amber-700",
  phone_bank: "bg-teal-100 text-teal-700",
  site_visit: "bg-purple-100 text-purple-700",
  delivery_route: "bg-orange-100 text-orange-700",
};
const WALK_STATUS_BADGE = {
  scheduled: "bg-slate-100 text-slate-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
};
const WALK_STATUS_LABELS = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};
const WALK_EMPTY_FORM = {
  name: "",
  type: "door_knock",
  district: "",
  assigned_user_ids: [],
  date: "",
  stops_total: "",
};

// Must be stable reference (outside component) to avoid remounting the map API
const GMAP_LIBS = ["places"];

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

const RouteBuilderInner = ({ stops, onStopsChange, onRouteCalculated }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAPS_KEY,
    libraries: GMAP_LIBS,
  });

  const [directions, setDirections] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [routeErr, setRouteErr] = useState("");
  const [userCenter, setUserCenter] = useState({ lat: 33.4484, lng: -112.074 });
  const acRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setUserCenter({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => {},
        { timeout: 6000 },
      );
    }
  }, []);

  const [csvLoading, setCsvLoading] = useState(false);
  const csvRef = useRef(null);

  const addStop = () => {
    if (!acRef.current) return;
    const place = acRef.current.getPlace();
    if (!place?.geometry) return;
    const stop = {
      address: place.formatted_address || place.name || "",
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      contact_name: "",
    };
    onStopsChange([...stops, stop]);
    if (inputRef.current) inputRef.current.value = "";
    setDirections(null);
    onRouteCalculated(null);
  };

  const removeStop = (idx) => {
    onStopsChange(stops.filter((_, i) => i !== idx));
    setDirections(null);
    onRouteCalculated(null);
  };

  const moveStop = (idx, dir) => {
    const next = [...stops];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    onStopsChange(next);
    setDirections(null);
    onRouteCalculated(null);
  };

  const updateStopName = (idx, name) => {
    const next = stops.map((s, i) =>
      i === idx ? { ...s, contact_name: name } : s,
    );
    onStopsChange(next);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setCsvLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target.result;
      const rows = text.split(/\r?\n/).filter(Boolean);
      const header = rows[0].toLowerCase();
      const hasHeader =
        header.includes("address") || header.includes("contact");
      const dataRows = hasHeader ? rows.slice(1) : rows;
      const parsed = dataRows
        .map((r) => {
          const cols = r.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
          const address = (cols[0] || "").replace(/^"|"$/g, "").trim();
          const contact_name = (cols[1] || "").replace(/^"|"$/g, "").trim();
          return { address, contact_name };
        })
        .filter((r) => r.address);

      const geocoder = new window.google.maps.Geocoder();
      const geocoded = [];
      for (const row of parsed) {
        await new Promise((resolve) => {
          geocoder.geocode({ address: row.address }, (results, status) => {
            if (status === "OK" && results[0]) {
              geocoded.push({
                address: results[0].formatted_address || row.address,
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
                contact_name: row.contact_name,
              });
            }
            resolve();
          });
        });
      }
      if (geocoded.length > 0) {
        onStopsChange([...stops, ...geocoded]);
        setDirections(null);
        onRouteCalculated(null);
      }
      setCsvLoading(false);
    };
    reader.readAsText(file);
  };

  const calcRoute = () => {
    if (stops.length < 2) return;
    setCalculating(true);
    setRouteErr("");
    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin: { lat: stops[0].lat, lng: stops[0].lng },
        destination: {
          lat: stops[stops.length - 1].lat,
          lng: stops[stops.length - 1].lng,
        },
        waypoints: stops.slice(1, -1).map((s) => ({
          location: { lat: s.lat, lng: s.lng },
          stopover: true,
        })),
        travelMode: window.google.maps.TravelMode.WALKING,
      },
      (result, status) => {
        setCalculating(false);
        if (status === "OK") {
          setDirections(result);
          const polyline = result.routes[0].overview_polyline.points;
          const routeUrl = `https://www.google.com/maps/dir/${stops
            .map((s) => encodeURIComponent(s.address))
            .join("/")}?travelmode=walking`;
          onRouteCalculated({ polyline, routeUrl });
        } else {
          setRouteErr(`Could not calculate route (${status})`);
        }
      },
    );
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading maps…
      </div>
    );
  }

  const mapCenter =
    stops.length > 0 ? { lat: stops[0].lat, lng: stops[0].lng } : userCenter;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Autocomplete
          onLoad={(ref) => (acRef.current = ref)}
          onPlaceChanged={addStop}
          options={{ types: ["address"] }}
          className="flex-1"
        >
          <input
            ref={inputRef}
            type="text"
            placeholder="Search address to add a stop…"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
          />
        </Autocomplete>
        <input
          ref={csvRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleCsvUpload}
        />
        <button
          type="button"
          onClick={() => csvRef.current?.click()}
          disabled={csvLoading}
          title="Upload CSV (address, contact_name)"
          className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40 whitespace-nowrap"
        >
          {csvLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          CSV
        </button>
      </div>

      {stops.length > 0 && (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {stops.map((s, i) => (
            <div
              key={i}
              className="bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="flex-1 truncate text-slate-700 text-xs">
                  {s.address}
                </span>
                <button
                  onClick={() => moveStop(i, -1)}
                  disabled={i === 0}
                  className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-20"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => moveStop(i, 1)}
                  disabled={i === stops.length - 1}
                  className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-20"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  onClick={() => removeStop(i)}
                  className="p-0.5 text-red-400 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <input
                type="text"
                value={s.contact_name || ""}
                onChange={(e) => updateStopName(i, e.target.value)}
                placeholder="Person or organization name (optional)"
                className="mt-1 ml-7 w-[calc(100%-1.75rem)] px-2 py-1 border border-slate-200 rounded text-xs text-slate-600 placeholder-slate-300 focus:border-blue-400 focus:outline-none bg-white"
              />
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg overflow-hidden border border-slate-200">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "240px" }}
          center={mapCenter}
          zoom={14}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {!directions &&
            stops.map(
              (s, i) =>
                s.lat && (
                  <Marker
                    key={i}
                    position={{ lat: s.lat, lng: s.lng }}
                    label={{
                      text: String(i + 1),
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  />
                ),
            )}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{ suppressMarkers: false }}
            />
          )}
        </GoogleMap>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={calcRoute}
          disabled={stops.length < 2 || calculating}
          className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
        >
          {calculating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Map className="w-3.5 h-3.5" />
          )}
          {calculating ? "Calculating…" : "Calculate Walking Route"}
        </button>
        {stops.length === 0 && (
          <span className="text-xs text-slate-400">Add stops above</span>
        )}
        {stops.length === 1 && (
          <span className="text-xs text-slate-400">
            Add at least one more stop
          </span>
        )}
      </div>

      {routeErr && <div className="text-xs text-red-600">{routeErr}</div>}
    </div>
  );
};

const RouteBuilder = (props) => {
  if (!MAPS_KEY)
    return (
      <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
        Google Maps key not configured — route builder unavailable.
      </div>
    );
  return <RouteBuilderInner {...props} />;
};

export const WalkPathManagement = ({ facility }) => {
  const fid = facility?.facility_id;
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editWalk, setEditWalk] = useState(null);
  const [form, setForm] = useState(WALK_EMPTY_FORM);
  const [stops, setStops] = useState([]);
  const [routeData, setRouteData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (!fid) return;
    setLoading(true);
    authFetch(`/facility-walks?facility_id=${encodeURIComponent(fid)}`)
      .then((data) => {
        if (Array.isArray(data)) setWalks(data);
      })
      .finally(() => setLoading(false));
    authFetch(`/facility-users?facility_id=${encodeURIComponent(fid)}`).then(
      (data) => {
        const list = Array.isArray(data) ? data : (data?.users ?? []);
        setTeamMembers(
          list.map((u) => ({
            user_id: u.user_id || u.id,
            full_name:
              u.full_name ||
              `${u.first_name || ""} ${u.last_name || ""}`.trim(),
            title: u.title || "",
          })),
        );
      },
    );
  }, [fid]);

  const openCreate = () => {
    setEditWalk(null);
    setForm(WALK_EMPTY_FORM);
    setStops([]);
    setRouteData(null);
    setFormOpen(true);
  };

  const openEdit = (walk) => {
    setEditWalk(walk);
    const existingUids = Array.isArray(walk.assignees)
      ? walk.assignees.map((a) => a.user_id)
      : walk.assigned_user_id
        ? [walk.assigned_user_id]
        : [];
    setForm({
      name: walk.name || "",
      type: walk.type || "door_knock",
      district: walk.district || "",
      assigned_user_ids: existingUids,
      date: walk.date || "",
      stops_total: walk.stops_total != null ? String(walk.stops_total) : "",
    });
    setStops(Array.isArray(walk.waypoints) ? walk.waypoints : []);
    setRouteData(
      walk.route_polyline
        ? { polyline: walk.route_polyline, routeUrl: walk.route_url }
        : null,
    );
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.date || saving) return;
    setSaving(true);
    setSaveError("");
    const assignedMembers = teamMembers.filter((m) =>
      form.assigned_user_ids.includes(m.user_id),
    );
    const assignedToDisplay =
      assignedMembers.length > 0
        ? assignedMembers.map((m) => m.full_name).join(", ")
        : "Unassigned";
    const assignees = assignedMembers.map((m) => ({
      user_id: m.user_id,
      name: m.full_name,
    }));
    const stopsTotal =
      stops.length > 0
        ? stops.length
        : form.stops_total
          ? parseInt(form.stops_total, 10)
          : null;
    const payload = {
      facility_id: fid,
      name: form.name.trim(),
      type: form.type,
      district: form.district.trim(),
      date: form.date,
      scheduled_at: form.date,
      assigned_user_ids: form.assigned_user_ids,
      stops_total: stopsTotal,
      waypoints: stops,
      route_polyline: routeData?.polyline || null,
      route_url: routeData?.routeUrl || null,
    };
    if (editWalk) {
      const res = await authFetch(`/facility-walks/${editWalk.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (res.error) {
        setSaveError(res.error);
      } else {
        setWalks((prev) =>
          prev.map((w) =>
            w.id === editWalk.id
              ? {
                  ...w,
                  name: form.name.trim(),
                  type: form.type,
                  district: form.district,
                  date: form.date,
                  stops_total: stopsTotal,
                  waypoints: stops,
                  route_polyline: routeData?.polyline || "",
                  route_url: routeData?.routeUrl || "",
                  assignees,
                  assigned_to: assignedToDisplay,
                }
              : w,
          ),
        );
        setFormOpen(false);
      }
    } else {
      const res = await authFetch("/facility-walks", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res.error) {
        setSaveError(res.error);
      } else if (res.id) {
        setWalks((prev) => [
          {
            id: res.id,
            name: form.name.trim(),
            type: form.type,
            district: form.district,
            date: form.date,
            status: "scheduled",
            stops_total: stopsTotal,
            stops_visited: 0,
            stops_contacted: 0,
            waypoints: stops,
            route_polyline: routeData?.polyline || "",
            route_url: routeData?.routeUrl || "",
            assignees,
            assigned_to: assignedToDisplay,
          },
          ...prev,
        ]);
        setFormOpen(false);
      }
    }
    setSaving(false);
  };

  const handleDelete = async (walk) => {
    if (!window.confirm(`Delete "${walk.name}"?`)) return;
    setDeleting(walk.id);
    await authFetch(
      `/facility-walks/${walk.id}?facility_id=${encodeURIComponent(fid)}`,
      { method: "DELETE" },
    );
    setWalks((prev) => prev.filter((w) => w.id !== walk.id));
    setDeleting(null);
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const filtered = walks.filter(
    (w) =>
      (w.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (w.assigned_to || "").toLowerCase().includes(search.toLowerCase()) ||
      (w.district || "").toLowerCase().includes(search.toLowerCase()),
  );

  const counts = {
    scheduled: walks.filter((w) => w.status === "scheduled").length,
    in_progress: walks.filter((w) => w.status === "in_progress").length,
    completed: walks.filter((w) => w.status === "completed").length,
    cancelled: walks.filter((w) => w.status === "cancelled").length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-800">
            Route Management
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {walks.length} routes · {facility?.facility_name}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New Route
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search routes, assignees, districts…"
          className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
        />
      </div>

      {/* Stats strip */}
      {walks.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { key: "scheduled", label: "Scheduled", color: "text-slate-600" },
            {
              key: "in_progress",
              label: "In Progress",
              color: "text-blue-600",
            },
            { key: "completed", label: "Completed", color: "text-green-600" },
            { key: "cancelled", label: "Cancelled", color: "text-red-500" },
          ].map(({ key, label, color }) => (
            <div
              key={key}
              className="bg-white border border-slate-200 rounded-lg p-3 text-center"
            >
              <div className={`text-xl font-bold ${color}`}>{counts[key]}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Walk list */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-slate-400 w-6 h-6" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-10">
          {walks.length === 0
            ? "No routes yet — create one to get started."
            : "No routes match your search."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((w) => {
            const progress =
              w.stops_total && w.stops_total > 0
                ? Math.min(
                    100,
                    Math.round((w.stops_visited / w.stops_total) * 100),
                  )
                : null;
            return (
              <div
                key={w.id}
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-semibold text-slate-800 text-sm">
                        {w.name}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${WALK_TYPE_BADGE[w.type] ?? "bg-slate-100 text-slate-600"}`}
                      >
                        {WALK_TYPE_LABELS[w.type] || w.type}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${WALK_STATUS_BADGE[w.status] ?? "bg-slate-100 text-slate-600"}`}
                      >
                        {WALK_STATUS_LABELS[w.status] || w.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {w.assigned_to || "Unassigned"}
                      </span>
                      {w.district && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {w.district}
                        </span>
                      )}
                      {w.date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {w.date}
                        </span>
                      )}
                    </div>
                    {w.stops_total != null && (
                      <div className="mt-2.5">
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                          <span>
                            {w.stops_visited} / {w.stops_total} stops
                          </span>
                          {progress != null && <span>{progress}%</span>}
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${progress ?? 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(w)}
                      title="Edit"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(w)}
                      disabled={deleting === w.id}
                      title="Delete"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                    >
                      {deleting === w.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit modal */}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !saving && setFormOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4 overflow-y-auto max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">
                {editWalk ? "Edit Route" : "New Route"}
              </h3>
              <button
                onClick={() => setFormOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4.5 h-4.5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Route Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="LD4 North Precinct Route"
                  autoFocus
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Type
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setField("type", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                  >
                    <option value="door_knock">Door Knock</option>
                    <option value="lit_drop">Lit Drop</option>
                    <option value="phone_bank">Phone Bank</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="delivery_route">Delivery Route</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setField("date", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    District
                  </label>
                  <input
                    value={form.district}
                    onChange={(e) => setField("district", e.target.value)}
                    placeholder="LD4-N"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Total Stops
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.stops_total}
                    onChange={(e) => setField("stops_total", e.target.value)}
                    placeholder="120"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Team Members
                  {form.assigned_user_ids.length > 0 && (
                    <span className="ml-1.5 text-blue-600 font-normal">
                      · {form.assigned_user_ids.length} selected
                    </span>
                  )}
                </label>
                <div className="border border-slate-200 rounded-lg max-h-36 overflow-y-auto divide-y divide-slate-100">
                  {teamMembers.length === 0 ? (
                    <p className="text-xs text-slate-400 px-3 py-2">
                      No team members yet
                    </p>
                  ) : (
                    teamMembers.map((m) => (
                      <label
                        key={m.user_id}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={form.assigned_user_ids.includes(m.user_id)}
                          onChange={(e) =>
                            setField(
                              "assigned_user_ids",
                              e.target.checked
                                ? [...form.assigned_user_ids, m.user_id]
                                : form.assigned_user_ids.filter(
                                    (id) => id !== m.user_id,
                                  ),
                            )
                          }
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700 flex-1">
                          {m.full_name}
                        </span>
                        {m.title && (
                          <span className="text-xs text-slate-400">
                            {m.title}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
                {form.assigned_user_ids.length === 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Unassigned — visible to all team members
                  </p>
                )}
              </div>

              {/* Route builder — interactive walking route planner */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-600">
                    Route Stops{" "}
                    {stops.length > 0 && (
                      <span className="text-blue-500">
                        · {stops.length} stop{stops.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </label>
                  {routeData?.routeUrl && (
                    <a
                      href={routeData.routeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Open in Maps
                    </a>
                  )}
                </div>
                <RouteBuilder
                  stops={stops}
                  onStopsChange={setStops}
                  onRouteCalculated={setRouteData}
                />
              </div>
            </div>

            {saveError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {saveError}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || !form.date || saving}
                className="flex-1 py-2.5 bg-blue-600 rounded-lg text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {saving
                  ? "Saving…"
                  : editWalk
                    ? "Save Changes"
                    : "Create Route"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Broadcasts Panel ──────────────────────────────────────────────────────────
